/**
 * CashRegisterQRModalService
 *
 * Imperative bridge to the mounted {@link ../components/CashRegisterQRModal}
 * host, mirroring {@link ./CashRegisterRetryDialogService}. The transport layer
 * ({@link ./cashierTransport}) calls {@link showCashRegisterQRModal} to display
 * the Fiscat payload as an on-screen QR code for the register to scan.
 *
 * @module CashRegisterQRModalService
 */

let activeQRModalHandler = null;

/**
 * Register the modal's open handler. Called once by the mounted host.
 *
 * @param {(options:object)=>Promise<boolean>} handler - Opens the modal and
 *        resolves `true` (scanned/confirmed) or `false` (deferred).
 * @returns {()=>void} Unregister function.
 */
export const registerCashRegisterQRModalHandler = (handler) => {
    activeQRModalHandler = handler;

    return () => {
        if (activeQRModalHandler === handler) {
            activeQRModalHandler = null;
        }
    };
};

/**
 * Show the on-screen Fiscat QR modal.
 *
 * @param {object} options
 * @param {string} options.payload - The Fiscat QR payload string to encode.
 * @param {string} [options.orderCode] - Order code shown above the QR.
 * @returns {Promise<boolean>} Resolves `true` when the operator confirms the
 *          register scanned the code, `false` when deferred / no host mounted.
 */
export const showCashRegisterQRModal = (options = {}) => {
    if (typeof activeQRModalHandler === 'function') {
        return activeQRModalHandler(options);
    }

    // No modal host mounted (e.g. headless background task) — cannot display a
    // QR code, so report "not shown" and let the caller defer the receipt.
    return Promise.resolve(false);
};
